import { useCallback, useEffect, useState } from "react";
import {
  alpha,
  Autocomplete,
  Box,
  Button,
  Divider,
  Grid2,
  IconButton,
  Modal,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Close, SettingsEthernet } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { emptyInterface, InterfaceFlow, InterfacePayload } from "../types";
import { useModalStyles } from "../../components/modalStyles";

type FormErrors = Partial<Record<keyof InterfacePayload, string>>;

interface ModalInterfaceProps {
  open: boolean;
  loading?: boolean;
  interfaceData?: InterfaceFlow | null;
  exportadorOptions?: string[];
  onClose: () => void;
  onSave: (payload: InterfacePayload, id?: number) => void;
}

export default function ModalInterface(props: ModalInterfaceProps) {
  const {
    open,
    loading = false,
    interfaceData,
    exportadorOptions = [],
    onClose,
    onSave,
  } = props;

  const [form, setForm] = useState<InterfacePayload>(emptyInterface);
  const [errors, setErrors] = useState<FormErrors>({});

  const theme = useTheme();
  const styles = useModalStyles(theme);
  const isEdit = Boolean(interfaceData?.id);

  const validate = useCallback((data: InterfacePayload): FormErrors => {
    const errs: FormErrors = {};

    if (!data.exportador?.trim()) errs.exportador = "Exportador é obrigatório";

    const index = data.index?.trim();
    if (index && !/^\d+$/.test(index)) errs.index = "Informe apenas números";

    return errs;
  }, []);

  useEffect(() => {
    if (!open) return;

    let initialData: InterfacePayload = emptyInterface;

    if (interfaceData) {
      const { id, ...rest } = interfaceData;
      initialData = { ...emptyInterface, ...rest };
    }

    setForm(initialData);
    setErrors(validate(initialData));
  }, [open, interfaceData, validate]);

  const handleChange = (field: keyof InterfacePayload, value: string) => {
    const next = { ...form, [field]: value };
    setForm(next);
    setErrors(validate(next));
  };

  const hasErrors = Object.keys(errors).length > 0;

  const handleSave = () => {
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    onSave(
      {
        exportador: form.exportador.trim(),
        interface: form.interface.trim(),
        index: form.index.trim(),
      },
      interfaceData?.id,
    );
  };

  const fields: {
    field: keyof InterfacePayload;
    label: string;
    placeholder: string;
    size: { xs: number; sm: number };
    select?: boolean;
  }[] = [
    {
      field: "exportador",
      label: "Exportador",
      placeholder: "Selecione o exportador",
      size: { xs: 12, sm: 12 },
      select: true,
    },
    {
      field: "interface",
      label: "Interface",
      placeholder: "Digite o nome da interface",
      size: { xs: 12, sm: 8 },
    },
    {
      field: "index",
      label: "Index",
      placeholder: "Digite o index",
      size: { xs: 12, sm: 4 },
    },
  ];

  return (
    <Modal open={open} onClose={loading ? () => {} : onClose}>
      <Paper sx={{ ...styles.paper, minHeight: "fit-content" }}>
        <Stack sx={styles.boxHeader}>
          <Stack sx={styles.boxTitle}>
            <Stack
              sx={{
                p: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.2),
                borderRadius: 1.5,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SettingsEthernet color="primary" />
            </Stack>
            <Stack>
              <Typography
                fontWeight="bold"
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: "1.05rem",
                  lineHeight: 1.2,
                }}
              >
                {isEdit ? "Editar interface" : "Adicionar interface"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {form.interface || form.exportador || "Nova interface"}
              </Typography>
            </Stack>
          </Stack>
          <IconButton aria-label="close" onClick={onClose} disabled={loading}>
            <Close />
          </IconButton>
        </Stack>

        <Divider sx={styles.divider} />

        <Grid2 container columnSpacing={1} sx={styles.boxFields}>
          {fields.map(({ field, label, placeholder, size, select }) => (
            <Grid2 size={size} key={field}>
              <Typography variant="subtitle2">{label}</Typography>
              {select ? (
                <Autocomplete
                  freeSolo
                  fullWidth
                  size="small"
                  options={exportadorOptions}
                  value={form[field] || null}
                  onChange={(_event, newValue) =>
                    handleChange(field, newValue ?? "")
                  }
                  onInputChange={(_event, newValue) =>
                    handleChange(field, newValue ?? "")
                  }
                  isOptionEqualToValue={(option, value) => option === value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={placeholder}
                      error={Boolean(errors[field])}
                      helperText={errors[field] ?? " "}
                    />
                  )}
                />
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  autoComplete="off"
                  placeholder={placeholder}
                  value={form[field] ?? ""}
                  onChange={(event) => handleChange(field, event.target.value)}
                  error={Boolean(errors[field])}
                  helperText={errors[field] ?? " "}
                />
              )}
            </Grid2>
          ))}
        </Grid2>

        <Box sx={styles.boxActions}>
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <LoadingButton
            variant="contained"
            loading={loading}
            disabled={hasErrors || loading}
            onClick={handleSave}
          >
            {isEdit ? "Salvar" : "Adicionar"}
          </LoadingButton>
        </Box>
      </Paper>
    </Modal>
  );
}
